import type { FormTheme } from '../types/forms';

const LENDNAV_USER_SHORT_ID = 'y8kqcc';

export const lendNavLogoAssets = {
  darkWordmark: '/brands/lendnav/lendnav-logo-dark-wordmark.png',
  lightWordmark: '/brands/lendnav/lendnav-logo-light-wordmark.png',
} as const;

export interface ApplicationBrandTheme {
  surfaceBackground: string;
  surfaceForeground: string;
  surfaceMutedForeground: string;
  progressActiveBackground: string;
  progressActiveForeground: string;
  progressInactiveBackground: string;
  progressInactiveForeground: string;
  progressConnectorActive: string;
  progressConnectorInactive: string;
  actionBackground: string;
  actionForeground: string;
  actionHoverBackground: string;
  actionSubtleBackground: string;
  focusColor: string;
  linkColor: string;
  formProgressActiveBackground: string;
  formProgressInactiveBackground: string;
  formProgressForeground: string;
  logoOnDarkUrl?: string;
  logoOnLightUrl?: string;
}

const getRouteUserShortId = () => {
  if (typeof window === 'undefined') return null;

  const match = window.location.pathname.match(/\/application\/([^/]+)/i);
  return match?.[1] || null;
};

const getHostname = () => {
  if (typeof window === 'undefined') return '';

  return window.location.hostname.toLowerCase();
};

export const isLendNavApplication = (userShortId?: string | null) => {
  const normalizedShortId = (userShortId || getRouteUserShortId() || '').trim().toLowerCase();
  const hostname = getHostname();

  return (
    normalizedShortId === LENDNAV_USER_SHORT_ID ||
    hostname === 'apply.lendnav.com' ||
    hostname.endsWith('.lendnav.com')
  );
};

export const getApplicationBrandTheme = (
  theme?: Partial<FormTheme> | null,
  userShortId?: string | null
): ApplicationBrandTheme => {
  if (isLendNavApplication(userShortId)) {
    return {
      surfaceBackground: '#191919',
      surfaceForeground: '#FFFFFF',
      surfaceMutedForeground: 'rgba(255, 255, 255, 0.68)',
      progressActiveBackground: '#FF9494',
      progressActiveForeground: '#191919',
      progressInactiveBackground: 'rgba(255, 255, 255, 0.22)',
      progressInactiveForeground: '#FFFFFF',
      progressConnectorActive: '#FF9494',
      progressConnectorInactive: 'rgba(255, 255, 255, 0.3)',
      actionBackground: '#FF9494',
      actionForeground: '#191919',
      actionHoverBackground: '#FF8585',
      actionSubtleBackground: 'rgba(255, 148, 148, 0.14)',
      focusColor: 'rgba(255, 148, 148, 0.45)',
      linkColor: '#191919',
      formProgressActiveBackground: '#FF9494',
      formProgressInactiveBackground: 'rgba(25, 25, 25, 0.16)',
      formProgressForeground: '#191919',
      logoOnDarkUrl: lendNavLogoAssets.lightWordmark,
      logoOnLightUrl: lendNavLogoAssets.darkWordmark,
    };
  }

  return {
    surfaceBackground: theme?.primary || '#1e40af',
    surfaceForeground: theme?.accent || '#FFFFFF',
    surfaceMutedForeground: 'rgba(255, 255, 255, 0.68)',
    progressActiveBackground: '#3b82f6',
    progressActiveForeground: '#FFFFFF',
    progressInactiveBackground: '#9ca3af',
    progressInactiveForeground: '#FFFFFF',
    progressConnectorActive: '#3b82f6',
    progressConnectorInactive: '#9ca3af',
    actionBackground: '#2563eb',
    actionForeground: '#FFFFFF',
    actionHoverBackground: '#1d4ed8',
    actionSubtleBackground: '#eff6ff',
    focusColor: '#3b82f6',
    linkColor: '#2563eb',
    formProgressActiveBackground: theme?.accent || '#FFFFFF',
    formProgressInactiveBackground: theme?.background || '#f3f4f6',
    formProgressForeground: theme?.primary || '#1e40af',
  };
};
